import { useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { toSlugs, hasWordChars } from "./slug";
import { NoticeBox } from "@/components/NoticeBox";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Converts a title or phrase into a clean, URL-friendly "slug" — lowercase, hyphenated, with special characters stripped out — the format used in web page addresses, since a URL with spaces, capital letters, or punctuation either breaks or gets awkwardly encoded by the browser.`,
  how: `Type or paste your title, choose your separator character, and copy the generated slug.`,
  tip: `Keep slugs reasonably short and focused on the main keywords of the title — a slug that's just a shortened, readable version of the title tends to work better for both readability and search engines than the full title verbatim, especially once a title runs long.`,
};

export default function SlugGenerator() {
  const [input, setInput] = useState("");
  const [separator, setSeparator] = useState("-");
  const [lowercase, setLowercase] = useState(true);
  const [maxLen, setMaxLen] = useState("");
  const [stop, setStop] = useState(false);
  const [amp, setAmp] = useState(true);
  const [uni, setUni] = useState(false);
  const [dedupe, setDedupe] = useState(false);

  const slug = useMemo(() => toSlugs(input, separator || "-", lowercase, { maxLength: Number(maxLen) || undefined, stopWords: stop, andForAmp: amp, keepUnicode: uni, dedupe }), [input, separator, lowercase, maxLen, stop, amp, uni, dedupe]);
  const outLines = slug.split("\n");
  const emptyLines = input.split(/\r?\n/).filter((l, i) => l.trim() && hasWordChars(l) && !outLines[i]).length;
  const noTextLines = input.split(/\r?\n/).filter((l, i) => l.trim() && !hasWordChars(l) && !outLines[i]).length;

  const copy = () => {
    void copyText(slug, "Copied to clipboard");
  };

  // Confirmed as a real, reported gap: a QA retest found this tool
  // still produced a live slug with no block at the daily limit.
  const { blocked } = useLogToolUsage(slug, undefined, { ownActions: true });


  return (
    <ToolContainer>
      <Label className="mb-2 block">
        Text (one title per line for bulk slugs)
      </Label>
      <Textarea
        placeholder="My Blog Post Title!"
        value={input}
        onChange={e => setInput(e.target.value)}
        className="min-h-[100px]"
      />
      <div className="flex flex-wrap items-center gap-6 mt-4">
        <div className="flex items-center gap-2">
          <Label htmlFor="slug-sep" className="text-sm">Separator</Label>
          <Input id="slug-sep"
            className="w-16"
            value={separator}
            // Genuinely allowed to be empty while typing -- the real
            // "-" fallback lives in the slug-generation logic above,
            // not here. Forcing a non-empty value on every keystroke
            // (the previous `|| "-"` here) meant backspacing to clear
            // the field before typing a replacement character
            // immediately snapped back to "-" on the backspace itself,
            // so the field could never actually be changed through
            // normal delete-then-type behavior -- confirmed the real,
            // reported bug.
            onChange={e => setSeparator(e.target.value.slice(0, 1))}
            onBlur={() => { if (!separator) setSeparator("-"); }}
            maxLength={1}
          />
        </div>
        <div className="flex items-center gap-2">
          <Switch id="slug-lower" checked={lowercase} onCheckedChange={setLowercase} />
          <Label htmlFor="slug-lower" className="text-sm">Lowercase</Label>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3">
        <div className="flex items-center gap-2"><Label className="text-sm">Max length</Label><Input aria-label="Max length" className="w-20" inputMode="numeric" placeholder="none" value={maxLen} onChange={e => setMaxLen(e.target.value.replace(/\D/g, "").slice(0, 3))} /></div>
        {([["Remove stop words", stop, setStop], ["& → and", amp, setAmp], ["Keep non-Latin letters", uni, setUni], ["Number duplicate lines (-2, -3)", dedupe, setDedupe]] as const).map(([l, v, set]) => (
          <div key={l} className="flex items-center gap-2"><Switch id={`slug-${l}`} checked={v} onCheckedChange={set} aria-label={l} /><Label htmlFor={`slug-${l}`} className="text-sm">{l}</Label></div>
        ))}
      </div>
      {emptyLines > 0 && <NoticeBox className="text-sm mt-2" testId="slug-warn">{emptyLines} line{emptyLines === 1 ? "" : "s"} could not be converted (left blank so the rest stay aligned). Turn on "Keep non-Latin letters" to keep them.</NoticeBox>}
      {noTextLines > 0 && <p className="text-sm mt-2 text-muted-foreground" data-testid="slug-notext">{noTextLines} line{noTextLines === 1 ? " has" : "s have"} no letters or numbers, so the slug is blank.</p>}
      {slug && !blocked && (
        <div className="mt-4">
          <Label className="mb-2 block">Result</Label>
          <Textarea aria-label="Result"
            readOnly
            value={slug}
            className="min-h-[100px] font-mono text-sm bg-muted/30"
          />
          <Button className="mt-3" onClick={copy}>
            Copy Result
          </Button>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
